import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Timer, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const TimesTableRace = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const grade = user?.grade_level || 7;
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(45);
  const [problem, setProblem] = useState(null);
  const [options, setOptions] = useState([]);
  const [feedback, setFeedback] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [answered, setAnswered] = useState(0);

  const generateProblem = () => {
    const maxA = grade >= 8 ? 20 : grade >= 7 ? 15 : 12;
    const maxB = grade >= 8 ? 15 : grade >= 7 ? 12 : 12;
    const a = Math.floor(Math.random() * maxA) + 2;
    const b = Math.floor(Math.random() * maxB) + 2;
    const answer = a * b;
    const wrongs = new Set();
    while (wrongs.size < 3) { const w = answer + (Math.floor(Math.random()*20)-10); if (w !== answer && w > 0) wrongs.add(w); }
    setOptions([...wrongs, answer].sort(() => Math.random()-0.5));
    setProblem({ a, b, answer });
    setFeedback(null);
  };

  useEffect(() => { generateProblem(); }, []);
  useEffect(() => {
    if (timeLeft <= 0) { setGameOver(true); return; }
    const t = setInterval(() => setTimeLeft(v => v-1), 1000);
    return () => clearInterval(t);
  }, [timeLeft]);

  const handleAnswer = (val) => {
    if (feedback || gameOver) return;
    const correct = val === problem.answer;
    setAnswered(a => a+1);
    if (correct) { setScore(s => s+10); setFeedback({ correct: true }); }
    else setFeedback({ correct: false, answer: problem.answer });
    setTimeout(generateProblem, 600);
  };

  if (gameOver) {
    return (
      <div className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-10 shadow-2xl max-w-md w-full text-center border-2 border-slate-100">
          <div className="text-7xl mb-4">🏁</div>
          <h2 className="text-4xl font-black text-[#1E293B] mb-2">Race Over!</h2>
          <p className="text-6xl font-black text-cyan-500 mb-2">{score}</p>
          <p className="text-lg text-[#64748B] mb-2">{answered} problems solved</p>
          <p className="text-[#64748B] mb-8">{score >= 100 ? 'Multiplication master!' : score >= 50 ? 'Great speed!' : 'Keep practicing!'}</p>
          <div className="space-y-3">
            <button onClick={() => { setScore(0); setTimeLeft(45); setGameOver(false); setAnswered(0); generateProblem(); }} className="w-full py-4 bg-cyan-500 text-white rounded-2xl font-bold border-b-4 border-cyan-700 active:border-b-0 active:translate-y-1 transition-all">Race Again</button>
            <button onClick={() => navigate('/games')} className="w-full py-4 bg-slate-100 text-[#1E293B] rounded-2xl font-bold">Back to Brain Arena</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-sky-50" data-testid="times-table-race-game">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate('/games')} className="flex items-center gap-2 text-[#64748B] font-semibold"><ArrowLeft className="w-5 h-5" />Back</button>
          <div className="flex gap-3">
            <div className={`px-4 py-2 rounded-2xl font-bold flex items-center gap-2 text-white ${timeLeft <= 10 ? 'bg-red-500 animate-pulse' : 'bg-orange-500'}`}><Timer className="w-5 h-5" />{timeLeft}s</div>
            <span className="bg-cyan-500 text-white px-4 py-2 rounded-2xl font-bold">{score}pts</span>
          </div>
        </div>
        <div className="bg-white rounded-3xl p-10 border-2 border-slate-100 shadow-xl text-center mb-6">
          <p className="text-sm font-bold text-[#64748B] uppercase mb-3">Multiply!</p>
          <p className="text-7xl font-black text-[#1E293B]">{problem?.a} x {problem?.b}</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {options.map((opt, i) => (
            <button key={i} onClick={() => handleAnswer(opt)} data-testid={`times-answer-${i}`}
              className={`p-6 rounded-2xl text-3xl font-black border-2 transition-all ${
                feedback && opt === problem.answer ? 'bg-green-100 border-green-500 text-green-700' :
                'bg-white border-slate-200 hover:border-cyan-400 text-[#1E293B] active:scale-95'
              }`}>{opt}</button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TimesTableRace;
